import type { UnknownAction } from "@reduxjs/toolkit";
import type { DrawerView } from "@/constants/views";

interface MenuEntry {
  label?: string;
  icon?: React.ReactNode;
  /** Most entries open an overlay by dispatching. */
  action?: UnknownAction;
  /** …but the branch picker is a context function, not an action. */
  onClick?: () => void;
  divider?: boolean;
}

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { useState } from "react";

import { DRAWER_VIEW, MODAL_VIEW, POLICY_DRAWERS } from "@/constants/views";
import { Z_INDEX } from "@/constants/zIndex";
import { useAppDispatch } from "@/store";
import { openDrawer, openModal } from "@/store/slices/commonSlice";
// ADDITION — branch (outlet) selection; see src/lib/branch.ts.
import { useBranch } from "@/context/BranchContext";

/**
 * Single entry point for every informational surface, so nothing is
 * reachable only by deep link.
 */
export default function AppMenu() {
  const dispatch = useAppDispatch();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // ADDITION. The landing card carries a branch chip, but /menu and every other
  // route reach the picker only from here. Renders nothing on a single-outlet
  // brand.
  const { branches, current: currentBranch, openPicker } = useBranch();

  const close = () => setAnchor(null);

  const go = (action: UnknownAction) => () => {
    close();
    dispatch(action);
  };

  const items: MenuEntry[] = [
    /*
      ADDITION, completed. The `useBranch()` values above were destructured and
      then used by nothing — the outlet picker had no entry here at all, which
      is what the comment on that hook says it is for ("`/menu` and every other
      route reach the picker only from here"). It also failed
      `no-unused-vars` and so blocked `next build` outright.

      `action`-less, because `openPicker` is a function and this list's entries
      are redux ACTIONS — a function cannot go through `dispatch`. `onClick`
      below handles both shapes.

      Hidden on a single-outlet brand, exactly as the hook's comment says.
    */
    ...(branches.length > 1
      ? [
          {
            label: currentBranch?.name
              ? `Branch: ${currentBranch.name}`
              : "Choose a branch",
            icon: <StorefrontOutlinedIcon fontSize="small" />,
            onClick: openPicker,
          },
          { divider: true },
        ]
      : []),
    {
      label: "Offers & rewards",
      icon: <LocalOfferOutlinedIcon fontSize="small" />,
      action: openDrawer(DRAWER_VIEW.OFFERS),
    },
    // No "Recent orders" entry: the original has no order-history LIST view.
    // History is the inline rail on the menu page; its drawer holds one order.
    {
      label: "Your details",
      icon: <PersonOutlineIcon fontSize="small" />,
      action: openDrawer(DRAWER_VIEW.CONSUMER_DETAILS),
    },
    { divider: true },
    {
      label: "Store status",
      icon: <StorefrontOutlinedIcon fontSize="small" />,
      action: openModal(MODAL_VIEW.STORE_STATUS),
    },
    {
      label: "Opening hours",
      icon: <AccessTimeIcon fontSize="small" />,
      action: openDrawer(DRAWER_VIEW.STORE_SCHEDULE),
    },
    {
      // `DELIVERY_INFO_MODAL` is address ENTRY in the original, not an
      // information panel; the areas list is the informational surface.
      label: "Delivery Areas",
      icon: <DeliveryDiningIcon fontSize="small" />,
      action: openDrawer(DRAWER_VIEW.DELIVERY_AREAS),
    },
    {
      label: "Reviews",
      icon: <StarBorderIcon fontSize="small" />,
      action: openDrawer(DRAWER_VIEW.REVIEWS),
    },
    { divider: true },
    {
      label: "About us",
      icon: <InfoOutlinedIcon fontSize="small" />,
      action: openDrawer(DRAWER_VIEW.ABOUT),
    },
    ...Object.entries(POLICY_DRAWERS)
      .filter(([view]) => view !== DRAWER_VIEW.ABOUT)
      .map(([view, policy]) => ({
        label: policy.title,
        icon: <PolicyOutlinedIcon fontSize="small" />,
        action: openDrawer(view as DrawerView),
      })),
  ];

  return (
    <>
      <IconButton
        aria-label="More"
        onClick={(event) => setAnchor(event.currentTarget)}
        edge="end"
        sx={{ width: 44, height: 44 }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        sx={{ zIndex: Z_INDEX.POPUP }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {items.map((item, index) =>
          item.divider ? (
            <Divider key={`divider-${index}`} />
          ) : (
            <MenuItem
              key={item.label}
              onClick={
                item.onClick
                  ? () => {
                      close();
                      item.onClick?.();
                    }
                  : go(item.action as UnknownAction)
              }
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          ),
        )}
      </Menu>
    </>
  );
}
